// src/TwoFAChallenge.js — AVS Cloud 2FA Login-Bestätigung
import React, { useState, useRef, useEffect } from 'react';

const S = {
  page:    { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', background: 'var(--bg)' },
  card:    { background: '#0d1520', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '16px', padding: '2.5rem 2rem', maxWidth: '400px', width: '100%', textAlign: 'center' },
  icon:    { fontSize: '2.5rem', marginBottom: '1rem' },
  title:   { fontFamily: 'var(--font-head)', fontSize: '1.5rem', fontWeight: 900, color: '#fff', marginBottom: '0.4rem' },
  sub:     { fontSize: '0.82rem', color: 'var(--muted)', marginBottom: '2rem', lineHeight: 1.6 },
  label:   { display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.5rem', textAlign: 'left' },
  input:   { display: 'block', width: '100%', padding: '0.85rem 1rem', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: '8px', color: '#fff', fontSize: '1.4rem', letterSpacing: '0.25em', outline: 'none', boxSizing: 'border-box', textAlign: 'center', fontFamily: 'monospace', marginBottom: '1rem' },
  inputB:  { display: 'block', width: '100%', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '1rem', letterSpacing: '0.1em', outline: 'none', boxSizing: 'border-box', textAlign: 'center', fontFamily: 'monospace', marginBottom: '1rem' },
  btn:     { width: '100%', padding: '0.85rem', background: 'var(--cyan)', border: 'none', borderRadius: '8px', color: '#0a0a0a', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'var(--font-head)', letterSpacing: '0.03em', marginBottom: '0.75rem', transition: 'opacity 0.2s' },
  err:     { background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.25)', borderRadius: '6px', padding: '0.6rem 0.9rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#ff8888' },
  link:    { background: 'none', border: 'none', color: 'var(--cyan)', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' },
  muted:   { fontSize: '0.78rem', color: 'var(--muted)', marginTop: '1rem' },
};

export default function TwoFAChallenge({ onSuccess }) {
  const [code,      setCode]      = useState('');
  const [backup,    setBackup]    = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, [useBackup]);

  const handleCode = (val) => {
    const cleaned = val.replace(/\D/g, '').slice(0, 6);
    setCode(cleaned);
    setError('');
    if (cleaned.length === 6) submit(cleaned, null);
  };

  const submit = async (totpCode, backupCode) => {
    setLoading(true);
    setError('');
    try {
      const body = backupCode
        ? { backup_code: backupCode }
        : { code: totpCode };
      const r = await fetch('/api/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok || !d.success) {
        setError(d.error || 'Ungültiger Code.');
        setCode('');
        setBackup('');
        setLoading(false);
        return;
      }
      // Session cookie is set — now get the token
      const sessR = await fetch('/api/auth/session', { credentials: 'include' });
      const sess  = await sessR.json();
      if (sess?.token) onSuccess(sess.token);
      else setError('Sitzung konnte nicht geladen werden. Bitte erneut einloggen.');
    } catch {
      setError('Netzwerkfehler. Bitte versuche es erneut.');
    }
    setLoading(false);
  };

  const submitBackup = () => {
    if (!backup.trim()) return;
    submit(null, backup.trim());
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.icon}>🔐</div>
        <h1 style={S.title}>Zwei-Faktor-Authentifizierung</h1>
        <p style={S.sub}>
          {useBackup
            ? 'Gib einen deiner Backup-Codes ein.'
            : 'Öffne deine Authenticator-App und gib den 6-stelligen Code ein.'}
        </p>

        {error && <div style={S.err}>{error}</div>}

        {!useBackup ? (
          <>
            <label style={S.label}>Authenticator-Code</label>
            <input
              ref={inputRef}
              style={{ ...S.input, opacity: loading ? 0.5 : 1 }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={code}
              onChange={e => handleCode(e.target.value)}
              disabled={loading}
              maxLength={6}
            />
            <button
              style={{ ...S.btn, opacity: (loading || code.length < 6) ? 0.5 : 1 }}
              onClick={() => submit(code, null)}
              disabled={loading || code.length < 6}
            >
              {loading ? 'Prüfe…' : 'Bestätigen'}
            </button>
            <button style={S.link} onClick={() => { setUseBackup(true); setError(''); setCode(''); }}>
              Backup-Code verwenden
            </button>
          </>
        ) : (
          <>
            <label style={S.label}>Backup-Code</label>
            <input
              ref={inputRef}
              style={{ ...S.inputB, opacity: loading ? 0.5 : 1 }}
              type="text"
              autoComplete="off"
              placeholder="XXXXX-XXXXX"
              value={backup}
              onChange={e => { setBackup(e.target.value.toUpperCase()); setError(''); }}
              disabled={loading}
            />
            <button
              style={{ ...S.btn, opacity: (loading || !backup.trim()) ? 0.5 : 1 }}
              onClick={submitBackup}
              disabled={loading || !backup.trim()}
            >
              {loading ? 'Prüfe…' : 'Backup-Code einlösen'}
            </button>
            <button style={S.link} onClick={() => { setUseBackup(false); setError(''); setBackup(''); }}>
              Zurück zum Code
            </button>
          </>
        )}

        <p style={S.muted}>
          <a href="/api/auth/discord" style={{ color: 'var(--muted)' }}>Erneut einloggen</a>
        </p>
      </div>
    </div>
  );
}
